import React, { forwardRef, useRef, useState, useEffect, type ChangeEvent } from 'react';
import { InputWrapper } from '../shared/InputWrapper/InputWrapper';
import { useNormalizedInputProps } from '../shared/hooks/useNormalizedInputProps';
import { textInputProps } from '../shared/utils/textInputProps';
import type { BaseInputProps } from '../shared/types/input.types';
import { useModalInputLayer } from '../../modal/ModalContext';
import { Tag, seg } from '../../tags/Tag';
import { Copy, Checkmark, View, ViewOff } from '@core/icons-react';
import { useCopyToClipboard } from '../../shared/hooks/useCopyToClipboard';

export interface TextInputActionButton {
  type: 'show' | 'copy' | 'custom';
  icon?: React.ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  title?: string;
}

export interface TextInputProps extends Omit<BaseInputProps<HTMLInputElement>, 'onChange'>, 
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof BaseInputProps<HTMLInputElement> | 'onChange' | 'type'> {
  /**
   * Specify the type of the `<input>`
   */
  type?: string;
  
  /**
   * Optionally provide an `onChange` handler that is called whenever input is updated
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  
  /**
   * Specify an optional value for the `autocomplete` property on the underlying `<input>`
   */
  autoComplete?: string;
  
  /**
   * Optionally provide a `onClear` handler that is called when the clear button is clicked
   */
  onClear?: () => void;
  
  /**
   * Specify which field layer to use for the background (1, 2, or 3)
   * field-01: Base layer (default)
   * field-02: Darker in light mode, lighter in dark mode
   * field-03: Maximum contrast from base
   */
  layer?: 1 | 2 | 3;
  
  /**
   * Optional description text that replaces the label
   */
  description?: React.ReactNode;
  
  /**
   * Optional right-aligned action (typically a link)
   */
  rightAction?: React.ReactNode;
  
  /**
   * Optional action buttons for common operations like show/hide and copy
   */
  actionButtons?: TextInputActionButton[];
  
  /**
   * Hide the error icon when invalid (keeps underline and error message)
   */
  hideErrorIcon?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    className,
    inputClassName,
    disabled = false,
    enableCounter = false,
    helperText,
    hideLabel,
    id,
    inline = false,
    invalid = false,
    invalidText,
    labelText,
    description,
    rightAction,
    actionButtons,
    layer,
    maxCount,
    onChange = () => {},
    onClick = () => {},
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    readOnly,
    required,
    size = 'md',
    type = 'text',
    value,
    defaultValue,
    warn = false,
    warnText,
    autoComplete = 'off',
    hideErrorIcon = false,
    ...rest
  },
  ref
) {
  const prefix = 'core';
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = ref || inputRef;
  
  // Get effective layer from modal context or user prop
  const effectiveLayer = useModalInputLayer(layer, 1);
  
  // Track text count for counter feature
  const getInitialTextCount = (): number => {
    const targetValue = defaultValue || value || inputRef.current?.value || '';
    return targetValue.toString().length;
  };
  
  const [textCount, setTextCount] = useState(getInitialTextCount());
  
  // State for action buttons
  const [isRevealed, setIsRevealed] = useState(false);
  const { isCopied, copy } = useCopyToClipboard();
  useEffect(() => {
    setTextCount(getInitialTextCount());
  }, [value, defaultValue, enableCounter]);
  
  // Handle action button functionality
  const handleShowToggle = () => {
    setIsRevealed(!isRevealed);
  };
  
  // Determine if we should use password mode for masking
  const hasShowButton = actionButtons?.some(btn => btn.type === 'show');
  const shouldMask = hasShowButton && !isRevealed;
  const effectiveType = shouldMask ? 'password' : type;
  
  // Always use the actual value - browser handles masking with password type
  const displayValue = value !== undefined ? value : defaultValue;
  
  const normalizedProps = useNormalizedInputProps({
    id,
    readOnly,
    disabled,
    invalid,
    invalidText,
    warn,
    warnText,
  });
  
  const textInputClasses = [
    `${prefix}-text-input`,
    normalizedProps.invalid && `${prefix}-text-input--invalid`,
    normalizedProps.warn && `${prefix}-text-input--warning`,
    hideErrorIcon && (normalizedProps.invalid || normalizedProps.warn) && `${prefix}-text-input--no-icon`,
    size && `${prefix}-text-input--${size}`,
    effectiveLayer && effectiveLayer !== 1 && `${prefix}-text-input--field-${effectiveLayer}`,
    inputClassName,
  ].filter(Boolean).join(' ');
  
  const sharedTextInputProps: any = {
    id,
    onChange: (evt: ChangeEvent<HTMLInputElement>) => {
      if (!normalizedProps.disabled) {
        setTextCount(evt.target.value?.length || 0);
        onChange(evt);
      }
    },
    onClick: (evt: React.MouseEvent<HTMLInputElement>) => {
      if (!normalizedProps.disabled) {
        onClick(evt);
      }
    },
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    type: effectiveType,
    ref: mergedRef,
    className: textInputClasses,
    title: placeholder,
    disabled: normalizedProps.disabled,
    readOnly,
    required,
    value: displayValue,
    defaultValue,
    autoComplete,
    'aria-describedby': helperText ? normalizedProps.helperId : undefined,
    'data-invalid': normalizedProps.invalid || undefined,
    ...rest,
  };
  
  // Add maxLength if counter is enabled
  if (enableCounter && maxCount) {
    sharedTextInputProps.maxLength = maxCount;
  }
  
  const input = actionButtons && actionButtons.length > 0 ? (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
      <input
        {...textInputProps({
          sharedTextInputProps,
          invalid: normalizedProps.invalid,
          invalidId: normalizedProps.invalidId,
          warn: normalizedProps.warn,
          warnId: normalizedProps.warnId,
        })}
        className={`${textInputClasses} core-text-input--with-actions`}
        style={{
          paddingRight: `${actionButtons.length * 2}rem`, // Add padding for buttons
          width: '100%'
        }}
      />
      {/* Gradient fade on right when text overflows */}
      <div 
        style={{
          position: 'absolute',
          right: `${actionButtons.length * 1.75}rem`, // Position before buttons
          top: '1px', // Start below top edge
          bottom: '1px', // Stop above bottom border
          width: '2rem',
          background: `linear-gradient(to right, transparent, var(--core-field-0${effectiveLayer}))`,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {/* Action buttons positioned inside the field */}
      <div 
        style={{
          position: 'absolute',
          right: '0.5rem',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          gap: '0.25rem',
          zIndex: 2,
          paddingLeft: '0.25rem'
        }}
      >
        {actionButtons.map((button, index) => {
          if (button.type === 'show') {
            const ShowIcon = isRevealed ? ViewOff : View;
            return (
              <Tag
                key={`action-btn-${index}`}
                template="action-sm"
                segments={[seg.icon(ShowIcon, { size: 16 })]}
                onClick={button.onClick || handleShowToggle}
                subtle
                title={button.title || (isRevealed ? 'Hide value' : 'Show value')}
              >
                {button.icon}
              </Tag>
            );
          } else if (button.type === 'copy') {
            return (
              <Tag
                key={`action-btn-${index}`}
                template="action-sm"
                segments={[seg.icon(isCopied ? Checkmark : Copy, { size: 16 })]}
                onClick={button.onClick || (() => copy(String(value || defaultValue || '')))}
                subtle
                title={button.title || 'Copy value'}
              >
                {button.icon}
              </Tag>
            );
          } else {
            return (
              <Tag
                key={`action-btn-${index}`}
                template="action-sm"
                onClick={button.onClick}
                subtle
                title={button.title || button.ariaLabel}
              >
                {button.icon}
              </Tag>
            );
          }
        })}
      </div>
    </div>
  ) : (
    <input
      {...textInputProps({
        sharedTextInputProps,
        invalid: normalizedProps.invalid,
        invalidId: normalizedProps.invalidId,
        warn: normalizedProps.warn,
        warnId: normalizedProps.warnId,
      })}
    />
  );
  
  return (
    <InputWrapper
      className={className}
      disabled={normalizedProps.disabled}
      enableCounter={enableCounter}
      helperText={helperText}
      hideLabel={hideLabel}
      id={id}
      inline={inline}
      invalid={normalizedProps.invalid}
      invalidText={invalidText}
      labelText={labelText}
      description={description}
      rightAction={rightAction}
      maxCount={maxCount}
      readOnly={readOnly}
      required={required}
      size={size}
      textCount={textCount}
      warn={normalizedProps.warn}
      warnText={warnText}
      hideErrorIcon={hideErrorIcon}
    >
      {input}
    </InputWrapper>
  );
});

TextInput.displayName = 'TextInput';
